import React from "react";
import { mimeLabel, formatDuration, formatBytes } from "../../utils/algoliaFormatters";
import Chip from "../common/Chip";

/**
 * The chips above the filter body: everything currently narrowing the results,
 * each one removable.
 *
 * Both halves of a chip used to be raw index internals. The field read
 * "projectIds:" / "mimetype:" because it was `field.replace("metadata.", "")`,
 * and the value fell through to the stored value for every facet except
 * mimetype — so an active TAG filter chipped its Firestore document id. The
 * facet list beside it was showing the tag's name the whole time, which made the
 * chip look like it belonged to some other filter entirely.
 *
 * `labelMaps` is how a caller supplies names for a facet whose buckets are ids;
 * anything without one falls back to the raw value, which is right for facets
 * that already bucket by words (authors, license).
 */

// Field → what a person calls it. Singular: one chip is one value.
const FIELD_LABELS = {
    "metadata.mimetype": "Type",
    "metadata.tags": "Tag",
    "metadata.authors": "Author",
    "metadata.license": "License",
    "metadata.projectIds": "Project",
    "metadata.duration": "Duration",
    "metadata.size": "Size",
};

const fieldLabel = (field) => FIELD_LABELS[field] ?? field.replace("metadata.", "");

/** Range chips read "1.4 MB – 20 MB", with an open end when only one side is set. */
function rangeLabel(field, bounds) {
    const format =
        field === "metadata.duration" ? formatDuration
        : field === "metadata.size" ? formatBytes
        : (v) => String(v);

    const hasMin = bounds.min !== null && bounds.min !== undefined;
    const hasMax = bounds.max !== null && bounds.max !== undefined;

    if (hasMin && hasMax) return `${format(bounds.min)} – ${format(bounds.max)}`;
    if (hasMin) return `≥ ${format(bounds.min)}`;
    if (hasMax) return `≤ ${format(bounds.max)}`;
    return "any";
}

/** What each derivation option is called once it's a chip. */
const DERIVATION_LABELS = {
    source: "source files",
    export: "exported files",
    stale: "out of date",
    "needs-export": "exports out of date",
};

export function ActiveFilters({
    facetFilters,
    numericFilters,
    noteFilter,
    derivationFilter,
    onRemoveFacet,
    onRemoveNumeric,
    onRemoveNoteFilter,
    onRemoveDerivationFilter,
    labelMaps = {},
}) {
    const facetChips = Object.entries(facetFilters).flatMap(([field, vals]) =>
        vals.map((value) => ({
            key: `facet:${field}:${value}`,
            field,
            label: fieldLabel(field),
            value:
                field === "metadata.mimetype"
                    ? mimeLabel(value)
                    : (labelMaps?.[field]?.[value] ?? value),
            onRemove: () => onRemoveFacet(field, value),
        })),
    );

    const numericChips = Object.entries(numericFilters).map(([field, bounds]) => ({
        key: `numeric:${field}`,
        field,
        label: fieldLabel(field),
        value: rangeLabel(field, bounds),
        onRemove: () => onRemoveNumeric(field),
    }));

    // The notes filter rides its own channel (it becomes a numeric filter on
    // noteSummary only at query time), so it is chipped separately.
    const noteChips = noteFilter
        ? [{
            key: "notes",
            field: "notes",
            label: "Notes",
            value: noteFilter === "open" ? "unresolved" : "has notes",
            onRemove: onRemoveNoteFilter,
        }]
        : [];

    // Same story as the notes filter: it only becomes a numeric filter on
    // `derivation.*` at query time, so it gets its own chip.
    const derivationChips = derivationFilter && DERIVATION_LABELS[derivationFilter]
        ? [{
            key: "derivation",
            field: "derivation",
            label: "Links",
            value: DERIVATION_LABELS[derivationFilter],
            onRemove: onRemoveDerivationFilter,
        }]
        : [];

    const chips = [...noteChips, ...derivationChips, ...facetChips, ...numericChips];
    if (chips.length === 0) return null;

    return (
        <div className="asp-active-filters">
            {chips.map((chip) => (
                <Chip
                    key={chip.key}
                    variant="accent"
                    onRemove={chip.onRemove}
                    removeLabel={`Remove ${chip.label} filter ${chip.value}`}
                    title={`${chip.label}: ${chip.value}`}
                >
                    <span className="asp-chip-field">{chip.label}</span>
                    <span className="asp-chip-value">{chip.value}</span>
                </Chip>
            ))}
        </div>
    );
}
